<template>
  <div class="ai-memory">
    <div class="ai-memory__intro">
      <h3>Память ассистента</h3>
      <p>
        Факты об организации, которые ассистент учитывает во всех ответах. Если данные мониторинга расходятся
        с памятью, ассистент опирается на данные.
        <template v-if="loaded && !canEdit">
          Изменять память могут администраторы.
        </template>
      </p>
    </div>

    <div
      v-if="canEdit"
      class="ai-segmented ai-memory__tabs"
      role="tablist"
    >
      <button
        v-for="item in tabs"
        :key="item.key"
        type="button"
        role="tab"
        :aria-selected="tab === item.key ? 'true' : 'false'"
        :class="{ 'is-active': tab === item.key }"
        @click="setTab(item.key)"
      >
        {{ tabLabel(item) }}
      </button>
    </div>

    <div class="ai-memory__toolbar">
      <input
        v-model="query"
        type="search"
        class="ai-input"
        placeholder="Поиск по записям"
        aria-label="Поиск по записям"
        @input="search"
      >
      <button
        v-if="canEdit && tab === 'active'"
        type="button"
        class="ai-button is-small is-ghost"
        :disabled="checking || !counts.active"
        title="Сравнить все записи между собой: повторы и противоречия"
        @click="runCheck"
      >
        <span
          v-if="checking"
          class="ai-spinner ai-spinner--small"
        />
        <ai-icon
          v-else
          name="search"
          :size="14"
        />
        {{ checking ? 'Проверяю…' : 'Проверить противоречия' }}
      </button>
    </div>

    <form
      v-if="canEdit && tab === 'active'"
      class="ai-memory__add"
      @submit.prevent="add"
    >
      <textarea
        v-model="draft.text"
        class="ai-input"
        rows="2"
        :maxlength="maxText"
        placeholder="Новый факт, например: «Орлов П.А. — инженер-конструктор, работает в AutoCAD»"
        aria-label="Новая запись"
        @keydown.ctrl.enter.prevent="add"
      />
      <div class="ai-memory__add-row">
        <input
          v-model="draft.subject"
          class="ai-input"
          :maxlength="maxSubject"
          placeholder="Тема (необязательно)"
          aria-label="Тема"
        >
        <span class="ai-memory__counter">{{ draft.text.length }}/{{ maxText }}</span>
        <button
          type="submit"
          class="ai-button is-primary is-small"
          :disabled="adding || !draft.text.trim()"
        >
          <span
            v-if="adding"
            class="ai-spinner ai-spinner--small"
          />
          <ai-icon
            v-else
            name="plus"
            :size="14"
          />
          {{ adding ? 'Проверяю…' : 'Добавить' }}
        </button>
      </div>
    </form>

    <p
      v-if="note"
      class="ai-memory__note ai-tone"
      :class="`is-${note.tone}`"
      role="status"
    >
      <ai-icon
        :name="note.tone === 'success' ? 'check' : 'alert'"
        :size="15"
      />
      <span>{{ note.text }}</span>
      <button
        type="button"
        class="ai-icon-button is-small"
        title="Скрыть"
        @click="note = null"
      >
        <ai-icon
          name="close"
          :size="13"
        />
      </button>
    </p>

    <p
      v-if="checking"
      class="ai-memory__muted"
    >
      Сравниваю записи между собой — это может занять несколько минут.
    </p>
    <section
      v-if="pairs && tab === 'active'"
      class="ai-memory__check"
    >
      <header class="ai-memory__check-header">
        <span>{{ pairs.length ? `Найдено пар: ${pairs.length}` : 'Повторов и противоречий не найдено.' }}</span>
        <button
          type="button"
          class="ai-link-button"
          @click="pairs = null"
        >
          Скрыть
        </button>
      </header>
      <div
        v-for="(pair, index) in pairs"
        :key="index"
        class="ai-memory__pair"
      >
        <p class="ai-memory__pair-reason">
          <span
            class="ai-memory__verdict ai-tone"
            :class="pair.verdict === 'duplicate' ? 'is-warning' : 'is-danger'"
          >{{ pair.verdict === 'duplicate' ? 'повторяют друг друга' : 'противоречат' }}</span>
          <span>{{ pair.reason }}</span>
        </p>
        <ul class="ai-memory__list">
          <memory-item
            v-for="side in [pair.a, pair.b]"
            :key="side.id"
            :item="side"
            :can-edit="canEdit"
            :run="act"
            show-status
          />
        </ul>
      </div>
    </section>

    <p
      v-if="loading && !items.length"
      class="ai-memory__muted"
    >
      <span class="ai-spinner ai-spinner--small" />
      Загружаю…
    </p>
    <p
      v-else-if="error"
      class="ai-memory__error"
    >
      {{ error }}
    </p>
    <ul
      v-else-if="items.length"
      class="ai-memory__list"
    >
      <memory-item
        v-for="item in items"
        :key="item.id"
        :item="item"
        :can-edit="canEdit"
        :run="act"
      />
    </ul>
    <p
      v-else-if="loaded"
      class="ai-memory__empty"
    >
      {{ emptyText }}
    </p>

    <div class="ai-memory__actions">
      <button
        type="button"
        class="ai-button"
        @click="$emit('close')"
      >
        Закрыть
      </button>
    </div>
  </div>
</template>

<script>
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import MemoryItem from './MemoryItem.vue';
  import { MAX_SUBJECT, MAX_TEXT, checkNote, hasConflict } from './memory';

  const TABS = [
    { key: 'active', label: 'Записи' },
    { key: 'pending', label: 'Предложения' },
    { key: 'archived', label: 'Архив' },
  ];
  const EMPTY = {
    active: 'В памяти пока нет записей.',
    pending: 'Новых предложений нет. Они появляются, когда в чате просят ассистента что-то запомнить.',
    archived: 'Архив пуст.',
  };
  const DONE = {
    save: 'Запись сохранена.',
    archive: 'Запись убрана в архив.',
    restore: 'Запись возвращена в память.',
    approve: 'Предложение сохранено в память.',
    reject: 'Предложение отклонено.',
    remove: 'Запись удалена.',
  };
  const RESOLVED = {
    replace: 'Старая запись заменена новой и убрана в архив.',
    keep_both: 'Сохранены обе записи.',
    merge: 'Записи объединены.',
    discard: 'Новая запись не сохранена.',
  };
  const SEARCH_DELAY_MS = 300;

  // Shared memory: everyone reads the active entries; administrators add, edit, archive and delete them,
  // decide on suggestions from chats and look for duplicates and contradictions.
  export default {
    name: 'MemoryPanel',
    components: { AiIcon, MemoryItem },
    inject: { aiMemory: { default: null } },
    data: () => ({
      tabs: TABS,
      tab: 'active',
      query: '',
      items: [],
      counts: {},
      canEdit: false,
      loading: false,
      loaded: false,
      error: '',
      note: null,
      draft: { text: '', subject: '' },
      adding: false,
      checking: false,
      pairs: null,
      maxText: MAX_TEXT,
      maxSubject: MAX_SUBJECT,
    }),
    computed: {
      emptyText() {
        if (this.query.trim()) return 'Ничего не найдено.';
        if (this.tab === 'active' && this.canEdit) {
          return `${EMPTY.active} Добавьте факт выше или попросите ассистента в чате: «Запомни, что…»`;
        }
        return EMPTY[this.tab];
      },
    },
    created() {
      this.seq = 0;
      // Until the first answer: administrators see their controls without a jump.
      this.canEdit = this.$store.getters['assistant/canConfigure'];
      this.reload();
    },
    beforeDestroy() {
      clearTimeout(this.searchTimer);
    },
    methods: {
      tabLabel(item) {
        const pending = this.counts.pending || 0;
        return item.key === 'pending' && pending ? `${item.label} (${pending})` : item.label;
      },
      setTab(key) {
        if (this.tab === key) return;
        this.tab = key;
        this.items = [];
        this.note = null;
        this.reload();
      },
      search() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.reload, SEARCH_DELAY_MS);
      },
      async reload() {
        this.seq += 1;
        const { seq } = this;
        this.loading = true;
        try {
          const { data } = await api.listMemory({ status: this.tab, q: this.query.trim() || undefined });
          if (seq !== this.seq) return;
          this.items = data.items || [];
          this.counts = data.counts || {};
          this.canEdit = !!data.canEdit;
          this.error = '';
          this.$store.commit('assistant/SET', { memoryCounts: this.counts });
        } catch (error) {
          if (seq !== this.seq) return;
          this.error = api.errorText(error, 'Не удалось загрузить память');
        } finally {
          if (seq === this.seq) {
            this.loading = false;
            this.loaded = true;
          }
        }
      },
      // Check results show entries as they were: keep them in step with the changes.
      patchPairs(...changed) {
        if (!this.pairs) return;
        changed.filter(Boolean).forEach((item) => {
          this.pairs = this.pairs.map((pair) => ({
            ...pair,
            a: pair.a.id === item.id ? item : pair.a,
            b: pair.b.id === item.id ? item : pair.b,
          }));
        });
      },
      // A conflict needs the administrator's decision (the dialog of the widget).
      async decide(item) {
        if (!this.aiMemory) return;
        const result = await this.aiMemory.resolve(item);
        if (result) {
          this.patchPairs(result.item, result.other);
          this.note = { tone: 'success', text: RESOLVED[result.action] };
        } else if (item.status === 'archived') {
          this.note = { tone: 'warning', text: 'Запись осталась в архиве: расхождение с другой записью не решено.' };
        } else {
          this.note = { tone: 'warning', text: 'Запись ждёт решения во вкладке «Предложения».' };
        }
        this.reload();
      },
      async add() {
        const text = this.draft.text.trim();
        if (!text || this.adding) return;
        this.adding = true;
        this.note = null;
        try {
          const { data } = await api.createMemory({ text, subject: this.draft.subject.trim() || undefined });
          this.draft = { text: '', subject: '' };
          this.reload();
          if (hasConflict(data.item)) await this.decide(data.item);
          else this.note = checkNote(data.check, 'Запись добавлена.') || { tone: 'success', text: 'Запись добавлена.' };
        } catch (error) {
          // A duplicate is refused with 409 and the reason.
          this.note = { tone: 'warning', text: api.errorText(error, 'Не удалось добавить запись') };
        } finally {
          this.adding = false;
        }
      },
      request(item, action, payload) {
        switch (action) {
          case 'save':
            return api.updateMemory(item.id, payload);
          case 'archive':
            return api.updateMemory(item.id, { status: 'archived' });
          case 'restore':
            return api.updateMemory(item.id, { status: 'active' });
          case 'approve':
            return api.approveMemory(item.id);
          case 'reject':
            return api.rejectMemory(item.id);
          default:
            return api.deleteMemory(item.id).then(() => ({ data: { item: { ...item, status: 'deleted' } } }));
        }
      },
      /** Runs an action of a MemoryItem; true when it is done (an inline edit closes). */
      async act(item, action, payload) {
        if (action === 'resolve') {
          await this.decide(item);
          return true;
        }
        this.note = null;
        let data;
        try {
          ({ data } = await this.request(item, action, payload));
        } catch (error) {
          this.note = { tone: 'danger', text: api.errorText(error, 'Не удалось изменить запись') };
          return false;
        }
        this.patchPairs(data.item);
        this.reload();
        const { check } = data;
        // An edited text that repeats another entry is not saved: the editor stays open.
        if (check && check.verdict === 'duplicate') {
          this.note = checkNote(check);
          return action !== 'save';
        }
        if (hasConflict(data.item)) {
          await this.decide(data.item);
          return true;
        }
        this.note = checkNote(check, DONE[action]) || { tone: 'success', text: DONE[action] };
        return true;
      },
      async runCheck() {
        this.checking = true;
        this.pairs = null;
        this.note = null;
        try {
          const { data } = await api.checkMemory();
          this.pairs = data.conflicts || [];
        } catch (error) {
          this.note = { tone: 'danger', text: api.errorText(error, 'Проверка не выполнена') };
        } finally {
          this.checking = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-memory {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    padding: 16px 18px;
    overflow-y: auto;
  }

  .ai-memory__intro {
    h3 {
      margin: 0 0 4px;
      color: var(--ui-text);
      font-size: 16px;
      font-weight: 500;
    }

    p {
      margin: 0;
      color: var(--ui-text-muted);
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .ai-memory__tabs {
    align-self: flex-start;
    max-width: 100%;
    overflow-x: auto;
  }

  .ai-memory__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    .ai-input {
      flex: 1 1 220px;
      min-width: 0;
    }
  }

  .ai-memory__add {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px dashed var(--ui-border-strong);
    border-radius: var(--ui-radius-sm);
  }

  .ai-memory__add-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 10px;

    .ai-input {
      flex: 1 1 200px;
      min-width: 0;
    }
  }

  .ai-memory__counter {
    color: var(--ui-text-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  .ai-memory__note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    padding: 7px 6px 7px 10px;
    border-radius: var(--ui-radius-sm);
    font-size: 13px;

    > .ai-icon {
      margin-top: 2px;
    }

    span {
      flex: 1;
      padding-top: 1px;
    }

    .ai-icon-button {
      width: 22px;
      height: 22px;
    }
  }

  .ai-memory__check {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);
    background: var(--ui-surface-soft);
  }

  .ai-memory__check-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    color: var(--ui-text);
    font-size: 13px;
    font-weight: 500;
  }

  .ai-memory__pair {
    display: flex;
    flex-direction: column;
    gap: 6px;

    & + & {
      padding-top: 10px;
      border-top: 1px solid var(--ui-border);
    }
  }

  .ai-memory__pair-reason {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
    line-height: 1.5;
  }

  .ai-memory__verdict {
    padding: 0 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .ai-memory__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .ai-memory__muted,
  .ai-memory__error,
  .ai-memory__empty {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-memory__error {
    color: var(--ui-danger);
  }

  .ai-memory__empty {
    padding: 18px 12px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface-soft);
    justify-content: center;
    line-height: 1.5;
    text-align: center;
  }

  .ai-memory__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: auto;
  }
</style>
